import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./TotalSales.css";
import { buildDeliveredSaleRows, getSaleDateKey, getTotalSalesSummaryUnit, groupProductSales, toSaleNumber } from "../utils/salesDisplay";

const API_BASE =
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_API_URL ||
  "https://techiohisab.com/api";

const SALES_API = `${API_BASE}/sales`;
const PRODUCT_SALE_PAGE_SIZE = 50;

const getTodayDate = () => new Date().toISOString().slice(0, 10);

const getCompactPageNumbers = (currentPage, pageCount) => {
  if (pageCount <= 8) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const firstPages = [1, 2, 3, 4, 5];
  const lastPages = [pageCount - 2, pageCount - 1, pageCount].filter((page) => page > 5);
  const pages = [...firstPages];
  const normalizedCurrentPage = Math.min(Math.max(Number(currentPage) || 1, 1), pageCount);

  if (normalizedCurrentPage > 6) {
    pages.push("gap-left");
  }

  if (normalizedCurrentPage > 5 && normalizedCurrentPage < pageCount - 2) {
    pages.push(normalizedCurrentPage);
  }

  if (normalizedCurrentPage < pageCount - 3) {
    pages.push("gap-right");
  }

  for (const page of lastPages) {
    if (!pages.includes(page)) pages.push(page);
  }

  return pages;
};

const toNumber = toSaleNumber;
const getDateKey = getSaleDateKey;

const formatDate = (value) => {
  const dateKey = getDateKey(value);
  if (!dateKey) return "-";

  const [year, month, day] = dateKey.split("-");
  if (!year || !month || !day) return dateKey;

  return `${day}-${month}-${year.slice(-2)}`;
};

const normalizeStatus = (value) => {
  const raw = String(value || "").trim();
  const key = raw.toUpperCase().replace(/[\s-]+/g, "_");
  if (["DELIVERED", "DELIVER", "DELIVERY"].includes(key)) return "Delivered";
  if (["CANCEL", "CANCELLED", "CANCELED"].includes(key)) return "Cancel";
  if (["RETURN", "RTO", "DTO"].includes(key)) return "Return";
  return raw || "-";
};

const getStatusClass = (status) => {
  switch (normalizeStatus(status)) {
    case "Delivered":
      return "total-sales-status total-sales-status-green";
    case "Cancel":
    case "Return":
      return "total-sales-status total-sales-status-orange";
    default:
      return "total-sales-status total-sales-status-gray";
  }
};

export default function ProductSale() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterFromDate, setFilterFromDate] = useState(getTodayDate);
  const [filterToDate, setFilterToDate] = useState(getTodayDate);
  const [draftFromDate, setDraftFromDate] = useState(getTodayDate);
  const [draftToDate, setDraftToDate] = useState(getTodayDate);
  const [filterSearch, setFilterSearch] = useState("");
  const [draftSearch, setDraftSearch] = useState("");
  const [salesPage, setSalesPage] = useState(1);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const response = await axios.get(SALES_API);
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Product sale error:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const handleSearch = () => {
    setFilterFromDate(draftFromDate);
    setFilterToDate(draftToDate);
    setFilterSearch(draftSearch.trim());
  };

  const handleFromDateChange = (value) => {
    setDraftFromDate(value);
    setFilterFromDate(value);
  };

  const handleToDateChange = (value) => {
    setDraftToDate(value);
    setFilterToDate(value);
  };

  const handleResetFilters = () => {
    setDraftFromDate("");
    setDraftToDate("");
    setDraftSearch("");
    setFilterFromDate("");
    setFilterToDate("");
    setFilterSearch("");
  };

  const displayRows = useMemo(() => {
    const normalizedRows = buildDeliveredSaleRows(rows);
    return groupProductSales(normalizedRows, {
      fromDate: filterFromDate,
      toDate: filterToDate,
      search: filterSearch,
    }).sort((a, b) => {
      const unitSort = toNumber(b.unit) - toNumber(a.unit);
      if (unitSort !== 0) return unitSort;
      return String(a.product || "").localeCompare(String(b.product || ""));
    });
  }, [filterFromDate, filterSearch, filterToDate, rows]);

  useEffect(() => {
    setSalesPage(1);
  }, [filterFromDate, filterSearch, filterToDate]);

  const salesPageCount = Math.max(1, Math.ceil(displayRows.length / PRODUCT_SALE_PAGE_SIZE));
  const paginatedRows = displayRows.slice(
    (salesPage - 1) * PRODUCT_SALE_PAGE_SIZE,
    salesPage * PRODUCT_SALE_PAGE_SIZE
  );

  const summaryCards = useMemo(
    () => [
      {
        label: "Product Type",
        value: displayRows.length,
        tone: "blue",
      },
      {
        label: "Total Unit",
        value: getTotalSalesSummaryUnit(buildDeliveredSaleRows(rows), {
          fromDate: filterFromDate,
          toDate: filterToDate,
        }),
        tone: "green",
      },
      {
        label: "Total Amount",
        value: `Rs.${displayRows.reduce((sum, row) => sum + toNumber(row.amount), 0).toFixed(2)}`,
        tone: "red",
      },
    ],
    [displayRows, filterFromDate, filterToDate, rows]
  );

  useEffect(() => {
    if (salesPage > salesPageCount) {
      setSalesPage(salesPageCount);
    }
  }, [salesPage, salesPageCount]);

  return (
    <div className="total-sales-page">
      <div className="total-sales-topbar">
        <div>
          <h2>Product Sale</h2>
        </div>

        <div className="total-sales-actions">
          <button type="button" className="total-sales-top-btn refresh" onClick={fetchRows}>
            Refresh
          </button>
          <button type="button" className="total-sales-top-btn back" onClick={() => navigate("/sales")}>
            Back To Sales
          </button>
        </div>
      </div>

      <div className="total-sales-summary-cards product-sale-summary-cards">
        {summaryCards.map((card) => (
          <div key={card.label} className={`total-sales-summary-card ${card.tone}`}>
            <span className="total-sales-summary-label">{card.label}</span>
            <span className="total-sales-summary-value">{card.value}</span>
          </div>
        ))}
      </div>

      <div className="total-sales-card">
        <div className="product-sale-filters">
          <input
            type="date"
            value={draftFromDate}
            onChange={(event) => handleFromDateChange(event.target.value)}
          />
          <input
            type="date"
            value={draftToDate}
            onChange={(event) => handleToDateChange(event.target.value)}
          />
          <input
            className="product-sale-search-input"
            type="search"
            placeholder="Search product"
            value={draftSearch}
            onChange={(event) => setDraftSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <button type="button" className="total-sales-top-btn refresh" onClick={handleSearch}>
            Search
          </button>
          <button type="button" className="total-sales-top-btn back" onClick={handleResetFilters}>
            Reset Filter
          </button>
        </div>

        <div className="total-sales-scroll">
          <table className="total-sales-table product-sale-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Product Name</th>
                <th>Total Unit</th>
                <th>Total Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="5" className="total-sales-empty">
                    Loading product sale...
                  </td>
                </tr>
              )}

              {!loading &&
                paginatedRows.map((row, index) => (
                  <tr key={`${row.id}-${(salesPage - 1) * PRODUCT_SALE_PAGE_SIZE + index}`}>
                    <td className="total-sales-date-cell">{formatDate(row.date)}</td>
                    <td className="total-sales-product-cell">{row.product}</td>
                    <td>{toNumber(row.unit)}</td>
                    <td className="total-sales-amount-cell">Rs.{toNumber(row.amount).toFixed(2)}</td>
                    <td>
                      <span className={getStatusClass(row.status)}>{row.status}</span>
                    </td>
                  </tr>
                ))}

              {!loading && displayRows.length === 0 && (
                <tr>
                  <td colSpan="5" className="total-sales-empty">
                    No product sale found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {displayRows.length > PRODUCT_SALE_PAGE_SIZE && (
          <div className="total-sales-pagination">
            <button
              type="button"
              className="total-sales-pagination-btn"
              disabled={salesPage === 1}
              onClick={() => setSalesPage((page) => Math.max(1, page - 1))}
            >
              Prev
            </button>
            {getCompactPageNumbers(salesPage, salesPageCount).map((page) =>
              typeof page === "string" ? (
                <span key={`product-sale-pagination-${page}`} className="total-sales-pagination-btn disabled">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  type="button"
                  className={`total-sales-pagination-btn ${salesPage === page ? "active" : ""}`}
                  onClick={() => setSalesPage(page)}
                >
                  {page}
                </button>
              )
            )}
            <span className="total-sales-pagination-current">
              Page {salesPage} of {salesPageCount}
            </span>
            <button
              type="button"
              className="total-sales-pagination-btn"
              disabled={salesPage === salesPageCount}
              onClick={() => setSalesPage((page) => Math.min(salesPageCount, page + 1))}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
